import React from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { useAsyncState, useAsyncStateWithIsLoading, useLocalStorage } from '@/utils/hooks';
import {
    EmailLayoutResolution,
    createLayoutOverride,
    deleteLayoutOverride,
    getEmailLayouts,
    resolveEmailLayout,
    updateLayoutOverride,
} from '@/services/emailTemplates';
import TemplateEditorPanel, { TemplateEditorTarget } from './TemplateEditorPanel';
import OverridesSummary, { ShowOverridesButton } from './OverridesSummary';
import { ScopeState, editScopeOf, languageLabel, useScopeInUrl, useScopeNamesFromResolution } from './shared';
import theme from './EmailTemplates.module.scss';

/** Layout equivalent of the content editor's target builder. Layouts have no name or subject. */
function buildTarget(scope: ScopeState, resolution: EmailLayoutResolution): TemplateEditorTarget {
    const editScope = editScopeOf(scope);
    const language = languageLabel(scope.languageCode);
    const common = {
        kind: 'layout' as const,
        hasSubject: false,
        subject: '',
        languageCode: scope.languageCode,
        projectId: scope.projectId,
        profileId: scope.profileId,
    };

    if (editScope === 'global') {
        return {
            ...common,
            key: `global-${resolution.global?.id}`,
            readOnly: true,
            isNew: false,
            template: resolution.global?.template ?? '',
            sourceNote: 'Default layout — used by every project without an override',
        };
    }

    if (editScope === 'profile') {
        const existing = resolution.profileOverride;
        return {
            ...common,
            key: `profile-${scope.profileId}-${scope.languageCode ?? 'any'}-${existing?.id ?? 'new'}`,
            readOnly: false,
            isNew: !existing,
            template: existing?.template ?? resolution.global?.template ?? '',
            sourceNote: existing
                ? `Seller profile layout for ${resolution.profileName} — ${language}`
                : `New seller profile layout for ${resolution.profileName} — ${language}, seeded from the default layout`,
        };
    }

    const existing = resolution.projectOverride;
    const seededFrom =
        resolution.source === 'project-override'
            ? 'this project’s layout for another language'
            : resolution.source === 'profile-override'
              ? 'the seller profile layout'
              : 'the default layout';
    return {
        ...common,
        key: `project-${scope.projectId}-${scope.languageCode ?? 'any'}-${existing?.id ?? 'new'}`,
        readOnly: false,
        isNew: !existing,
        template: existing?.template ?? resolution.template ?? '',
        sourceNote: existing
            ? `Project layout for ${resolution.projectName} — ${language}`
            : `New project layout for ${resolution.projectName} — ${language}, seeded from ${seededFrom}`,
    };
}

function overrideAtScope(scope: ScopeState, resolution: EmailLayoutResolution) {
    const editScope = editScopeOf(scope);
    if (editScope === 'project') {
        return resolution.projectOverride;
    }
    if (editScope === 'profile') {
        return resolution.profileOverride;
    }
    return null;
}

/**
 * The email layout — the MJML document every content template is wrapped in. Same scope model as the
 * content editor: the bar picks profile / project / language, and the editor edits that scope.
 */
const LayoutsView: React.FC = () => {
    const [scope, setScope] = useScopeInUrl();
    // Remembered across templates and visits, like the preview toggle.
    const [showOverrides, setShowOverrides] = useLocalStorage('emailTemplateShowOverrides', true);

    const [layouts, , reloadLayouts] = useAsyncState(() => getEmailLayouts(), [], null);

    const [resolution, , reloadResolution, isResolving] = useAsyncStateWithIsLoading(
        () =>
            resolveEmailLayout({
                projectId: scope.projectId,
                profileId: scope.profileId,
                languageCode: scope.languageCode,
            }),
        [scope.projectId, scope.profileId, scope.languageCode],
        null
    );

    // Deep links carry only ids; the resolution response supplies the picker labels for them.
    useScopeNamesFromResolution(scope, setScope, resolution);

    if (!layouts || !resolution) {
        return <LoadingIndicator />;
    }

    const reload = async () => {
        await Promise.all([reloadLayouts(), reloadResolution()]);
    };

    const existing = overrideAtScope(scope, resolution);

    const handleSave = async (values: { template: string }) => {
        if (existing) {
            await updateLayoutOverride(existing.id, { template: values.template });
        } else {
            await createLayoutOverride({
                languageCode: scope.languageCode,
                // Exactly one scope: the most specific selection in the bar.
                projectId: editScopeOf(scope) === 'project' ? scope.projectId : null,
                profileId: editScopeOf(scope) === 'profile' ? scope.profileId : null,
                template: values.template,
            });
        }

        await reload();
    };

    const handleDelete = existing
        ? async () => {
              await deleteLayoutOverride(existing.id);
              await reload();
          }
        : undefined;

    return (
        <div>
            <p className={theme.hint}>
                The layout wraps every email. Content templates are injected into it as <code>{'{{{body}}}'}</code>.
            </p>

            <div className={theme.detailsLayout}>
                {showOverrides && (
                    <OverridesSummary
                        overrides={layouts.overrides}
                        activeId={existing?.id ?? null}
                        onHide={() => setShowOverrides(false)}
                        onSelect={(override) =>
                            setScope({
                                projectId: override.projectId,
                                projectName: override.projectId ? override.scopeName : null,
                                profileId: override.profileId,
                                profileName: override.profileId ? override.scopeName : null,
                                languageCode: override.languageCode,
                            })
                        }
                    />
                )}

                <div className={theme.detailsMain}>
                    <TemplateEditorPanel
                        target={buildTarget(scope, resolution)}
                        // The shown resolution may still belong to the previous scope while the new one
                        // loads; acting on it then would hit the wrong override row.
                        actionsDisabled={isResolving}
                        headerActions={
                            !showOverrides && (
                                <ShowOverridesButton
                                    count={layouts.overrides.length}
                                    onClick={() => setShowOverrides(true)}
                                />
                            )
                        }
                        onSave={handleSave}
                        onDelete={handleDelete}
                    />
                </div>
            </div>
        </div>
    );
};

export default LayoutsView;
